import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { getStartAndEndOfDay } from "../../extras";

// REDUX
import { useDispatch, useSelector } from "react-redux";
import {
  acceptOrderThunk,
  finishOrderThunk,
  getOrdersHistoryThunk,
  readyToPickupOrderThunk,
  resetOrdersSlice,
  selectAllOrders,
  setShowOrderInfosModal,
} from "../../store/actions/ordersSlice";
import { setSelectOrderDriverModalOrder } from "../../store/actions/driversSlice";

// SERVICES & API
import { getOrdersHistoryReport } from "../../api/order";

// APP COMPONENTS
import OrdersRow from "./table/ordersRow";
import HistoryReportSummary from "./reports/HistoryReportSummary";

// FILTERS
import DateFilter from "../search/dateFilter";
import SearchFilter from "../search/searchFilter";
import MerchantFilter from "../search/merchantFilter";
import PlatformFilter from "../search/platformFilter";
import OrderTypeFilter from "../search/orderTypeFilter";
import OrderStatusFilter from "../search/orderStatusFilter";
import PaymentMethodFilter from "../search/paymentMethodFilter";
import DeliveryOperatorFilter from "../search/deliveryOperatorFilter";
import OrderCodeOrCustomerNameFilter from "../search/orderCodeOrCustomerNameFilter";

// MATERIAL UI COMPONENTS
import {
  Alert,
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Skeleton,
  Typography,
  useTheme,
} from "@mui/material";

// ICONS
import ExpandCircleDownIcon from "@mui/icons-material/ExpandCircleDown";
import SearchIcon from "@mui/icons-material/Search";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";

// ROUTING
import { useHistory, useLocation } from "react-router-dom";
import { setIdUrlQueryParam } from "../../utils/routes";
import { setDateRange } from "../../utils/filters";
import PageHeaderLight from "../ui/PageHeaderLight";

// --- Animation Variants ---

const fadeIn = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 400, damping: 30 },
  },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.04, delayChildren: 0.1 },
  },
};

const listItem = {
  hidden: { opacity: 0, y: 8 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 500, damping: 35 },
  },
  exit: {
    opacity: 0,
    x: -16,
    transition: { duration: 0.15 },
  },
};

// --- Skeleton Loader ---

const OrderListSkeleton = () => (
  <Paper
    elevation={0}
    sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, overflow: "hidden" }}
  >
    {[...Array(5)].map((_, i) => (
      <Box key={i} sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center" }}>
        <Skeleton variant="circular" width={40} height={40} />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="60%" height={24} />
          <Skeleton variant="text" width="35%" height={18} />
        </Box>
        <Skeleton variant="rectangular" width={80} height={32} sx={{ borderRadius: 1 }} />
      </Box>
    ))}
  </Paper>
);

// --- Component ---

export default function OrdersReports() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  // Redux state
  const orders = useSelector(selectAllOrders);
  const showButtonMore = useSelector((state) => state.orders.showButtonMore);
  const showOrderInfosModal = useSelector(
    (state) => state.orders.showOrderInfosModal
  );
  const showSelectOrderDriverModal = useSelector(
    (state) => state.drivers.showSelectOrderDriverModal
  );
  const page = useSelector((state) => state.orders.page);

  // Local state
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [reportLoading, setReportLoading] = useState(true);
  const [filtersVisible, setFiltersVisible] = useState(true);
  const [error, setError] = useState(null);
  const [reportModalOpen, setReportModalOpen] = useState(false);

  // Filter state
  const [filters, setFilters] = useState({
    newSearch: false,
    query_text: "",
    dateStart: getStartAndEndOfDay().dateStart,
    dateEnd: getStartAndEndOfDay().dateEnd,
    platform: "ALL",
    merchant: "ALL",
    operator: { label: "Todos", value: "ALL" },
    payment_method: "ALL",
    order_type: "ALL",
    order_status: "ALL",
  });

  // Report data
  const [reportData, setReportData] = useState();

  // Derived: count active filters (non-"ALL" values, excluding meta fields)
  const activeFilterCount = useMemo(() => {
    const ignoredKeys = ["newSearch", "query_text", "dateStart", "dateEnd"];
    return Object.entries(filters).filter(([key, val]) => {
      if (ignoredKeys.includes(key)) return false;
      if (typeof val === "object" && val?.value) return val.value !== "ALL";
      return val !== "ALL";
    }).length;
  }, [filters]);

  // --- Effects ---

  useEffect(() => {
    handleInitialParam();
    search();
    return () => dispatch(resetOrdersSlice());
  }, []);

  useEffect(() => {
    if (!showOrderInfosModal) {
      setIdUrlQueryParam(null, location, history);
    }
  }, [showOrderInfosModal]);

  // --- Handlers ---

  const handleInitialParam = () => {
    const searchParams = new URLSearchParams(location.search);
    const id = searchParams.get("id");
    if (id) handleShowHideOrderModal(id);
  };

  const search = async (filtersData) => {
    setLoading(true);
    setError(null);

    try {
      const filtersObj = {
        newSearch: true,
        query_text: filtersData
          ? filtersData[OrderCodeOrCustomerNameFilter?.name] || ""
          : "",
        platform: filtersData
          ? filtersData[PlatformFilter.name] || "ALL"
          : "ALL",
        merchant: filtersData
          ? filtersData[MerchantFilter.name] || "ALL"
          : "ALL",
        operator: filtersData
          ? filtersData[DeliveryOperatorFilter.name] || filters.operator
          : filters.operator,
        payment_method: filtersData
          ? filtersData[PaymentMethodFilter.name] || "ALL"
          : "ALL",
        order_type: filtersData
          ? filtersData[OrderTypeFilter.name] || "ALL"
          : "ALL",
        order_status:
          filtersData && filtersData[OrderStatusFilter.name]?.value
            ? filtersData[OrderStatusFilter.name]?.value
            : "ALL",
        ...setDateRange(filtersData, DateFilter.name, { useISOFormat: true }),
      };

      setFilters(filtersObj);

      await Promise.all([
        loadOrdersHistory(true, filtersData),
        loadReportInfos(filtersData),
      ]);

      setSubmitted(true);
    } catch (e) {
      setError("Erro ao buscar pedidos. Tente novamente.");
      console.error("Search error:", e);
    } finally {
      setLoading(false);
    }
  };

  const loadOrdersHistory = async (newSearch, filtersData = null) => {
    try {
      setRefreshing(true);

      const baseFilters = {
        query_text: filters.query_text || undefined,
        dateStart: filters.dateStart || getStartAndEndOfDay().dateStart,
        dateEnd: filters.dateEnd || getStartAndEndOfDay().dateEnd,
        platform: filters.platform || "ALL",
        merchant: filters.merchant || "ALL",
        operator: filters.operator || { value: "ALL" },
        payment_method: filters.payment_method || "ALL",
        order_type: filters.order_type || "ALL",
        order_status: filters.order_status || "ALL",
      };

      const dateStart =
        baseFilters.dateStart !== "ALL"
          ? baseFilters.dateStart
          : new Date(new Date().setHours(0, 0, 0, 0)).toISOString();

      const dateEnd =
        baseFilters.dateEnd !== "ALL"
          ? baseFilters.dateEnd
          : new Date(new Date().setHours(23, 59, 59, 999)).toISOString();

      const paramPage = newSearch ? 1 : page + 1;

      let params = {
        page: paramPage,
        query_text: baseFilters.query_text,
        start_date: dateStart,
        end_date: dateEnd,
        platform: baseFilters.platform ?? "ALL",
        merchant: baseFilters.merchant ?? "ALL",
        operator: baseFilters.operator.value ?? "ALL",
        payment_method: baseFilters.payment_method ?? "ALL",
        order_type: baseFilters.order_type ?? "ALL",
        order_status: baseFilters.order_status ?? "ALL",
      };

      if (filtersData) {
        params = {
          page: 1,
          newSearch: true,
          query_text:
            filtersData[OrderCodeOrCustomerNameFilter?.name] ?? undefined,
          start_date:
            filtersData[DateFilter.name]?.dateStartString ||
            new Date(new Date().setHours(0, 0, 0, 0)).toISOString(),
          end_date:
            filtersData[DateFilter.name]?.dateEndString ||
            new Date(new Date().setHours(23, 59, 59, 999)).toISOString(),
          platform: filtersData[PlatformFilter.name]?.value ?? "ALL",
          merchant: filtersData[MerchantFilter.name]?.value ?? "ALL",
          operator:
            filtersData[DeliveryOperatorFilter.name]?.value ??
            baseFilters.operator.value,
          payment_method:
            filtersData[PaymentMethodFilter.name]?.value ?? "ALL",
          order_type: filtersData[OrderTypeFilter.name]?.value ?? "ALL",
          order_status: filtersData[OrderStatusFilter.name]?.value ?? "ALL",
        };
      }

      await dispatch(getOrdersHistoryThunk({ params, newSearch }));
    } catch (error) {
      console.error("Load orders error:", error);
      setError("Erro ao carregar pedidos. Tente novamente.");
    } finally {
      setRefreshing(false);
    }
  };

  const loadReportInfos = async (filtersData = null) => {
    try {
      setReportLoading(true);

      const dateStart = filters.dateStart
        ? filters.dateStart
        : getStartAndEndOfDay().dateStart;

      const dateEnd = filters.dateEnd
        ? filters.dateEnd
        : getStartAndEndOfDay().dateEnd;

      let params = {
        page: page,
        query_text: filters.query_text,
        start_date: dateStart,
        end_date: dateEnd,
        platform: filters.platform ?? "ALL",
        merchant: filters.merchant ?? "ALL",
        operator: filters.operator.value ?? "ALL",
        payment_method: filters.payment_method ?? "ALL",
        order_type: filters.order_type ?? "ALL",
        order_status: filters.order_status ?? "ALL",
      };

      if (filtersData) {
        params = {
          page: 1,
          newSearch: true,
          query_text:
            filtersData[OrderCodeOrCustomerNameFilter.name] ?? undefined,
          start_date:
            filtersData[DateFilter.name]?.dateStartString ||
            new Date(new Date().setHours(0, 0, 0, 0)).toISOString(),
          end_date:
            filtersData[DateFilter.name]?.dateEndString ||
            new Date(new Date().setHours(23, 59, 59, 999)).toISOString(),
          platform: filtersData[PlatformFilter.name]?.value ?? "ALL",
          merchant: filtersData[MerchantFilter.name]?.value ?? "ALL",
          operator:
            filtersData[DeliveryOperatorFilter.name]?.value ??
            filters.operator.value,
          payment_method:
            filtersData[PaymentMethodFilter.name]?.value ?? "ALL",
          order_type: filtersData[OrderTypeFilter.name]?.value ?? "ALL",
          order_status: filtersData[OrderStatusFilter.name]?.value ?? "ALL",
        };
      }

      const { data } = await getOrdersHistoryReport(params);
      setReportData(data);
    } catch (error) {
      console.error("Report loading error:", error);
    } finally {
      setReportLoading(false);
    }
  };

  const handleOrderAction = useCallback(
    (action, order) => {
      dispatch(action({ order }));
      loadOrdersHistory(false);
    },
    [dispatch]
  );

  const handleShowHideOrderModal = useCallback(
    (order_id) => {
      dispatch(
        setShowOrderInfosModal({
          orderId: order_id,
          show: !showOrderInfosModal,
        })
      );
    },
    [dispatch, showOrderInfosModal]
  );

  const toggleFilters = () => setFiltersVisible(!filtersVisible);

  // --- Page Header ---

  const pageHeaderActions = [
    {
      key: "relatorio",
      variant: "outlined",
      startIcon: <AssessmentOutlinedIcon />,
      onClick: () => setReportModalOpen(true),
      disabled: !submitted || reportLoading,
      children: "Relatório",
    },
    {
      key: "filtros",
      variant: filtersVisible ? "contained" : "outlined",
      startIcon: <FilterListIcon />,
      onClick: toggleFilters,
      children: (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          Filtros
          {activeFilterCount > 0 && !filtersVisible && (
            <Chip
              label={activeFilterCount}
              size="small"
              sx={{
                height: 20,
                minWidth: 20,
                fontSize: "0.7rem",
                fontWeight: 700,
                bgcolor: theme.palette.primary.main,
                color: "white",
              }}
            />
          )}
        </Box>
      ),
    },
  ];

  // --- Order Rows ---

  const orderRows = useMemo(() => {
    const handleSelectDriver = (item) => {
      dispatch(
        setSelectOrderDriverModalOrder({
          show: !showSelectOrderDriverModal,
          order: item,
        })
      );
    };

    const handleOrderModal = (order) => {
      const { order_id } = order;
      setIdUrlQueryParam(order_id, location, history);
      handleShowHideOrderModal(order_id);
    };

    return orders.map((item, index) => (
      <motion.div
        key={item.order_id || index}
        variants={listItem}
        layout
      >
        <OrdersRow
          item={item}
          showCenter
          onlyModalAction={false}
          acceptOrder={() => handleOrderAction(acceptOrderThunk, item)}
          readyToPickupOrder={() =>
            handleOrderAction(readyToPickupOrderThunk, item)
          }
          finishOrder={() => handleOrderAction(finishOrderThunk, item)}
          handleOrderModal={() => handleOrderModal(item)}
          handleSelectDriver={(e) => handleSelectDriver(item, e)}
        />
      </motion.div>
    ));
  }, [
    orders,
    dispatch,
    handleOrderAction,
    handleShowHideOrderModal,
    showSelectOrderDriverModal,
  ]);

  // --- Empty State ---

  const renderEmptyState = () => (
    <motion.div variants={fadeIn} initial="hidden" animate="visible">
      <Box
        sx={{
          py: { xs: 6, md: 10 },
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 3,
          }}
        >
          {submitted ? (
            <SearchOffIcon
              sx={{ fontSize: 36, color: theme.palette.text.disabled }}
            />
          ) : (
            <InboxOutlinedIcon
              sx={{ fontSize: 36, color: theme.palette.primary.main }}
            />
          )}
        </Box>

        <Typography
          variant="h6"
          sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 1 }}
        >
          {submitted
            ? "Nenhum pedido encontrado"
            : "Aplique filtros para ver os pedidos"}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            maxWidth: 420,
            mb: 4,
            lineHeight: 1.6,
          }}
        >
          {submitted
            ? "Tente ajustar os critérios de busca ou o período selecionado."
            : "Selecione um período e outros filtros para visualizar os pedidos e gerar relatórios."}
        </Typography>

        <Button
          variant={submitted ? "outlined" : "contained"}
          startIcon={submitted ? <TuneIcon /> : <SearchIcon />}
          onClick={toggleFilters}
          sx={{ borderRadius: 2, px: 3, py: 1 }}
        >
          {submitted ? "Ajustar Filtros" : "Aplicar Filtros"}
        </Button>
      </Box>
    </motion.div>
  );

  // --- Render ---

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, maxWidth: 1800, mx: "auto" }}>
      {/* Header */}
      <PageHeaderLight
        title="Pedidos"
        subtitle="Histórico de Pedidos"
        actions={pageHeaderActions}
        filters={
          <SearchFilter
            title=""
            filters={[
              OrderCodeOrCustomerNameFilter,
              PlatformFilter,
              OrderTypeFilter,
              OrderStatusFilter,
              DeliveryOperatorFilter,
              MerchantFilter,
              PaymentMethodFilter,
            ]}
            filterDate
            shouldCenter={false}
            submit={search}
            initialExpanded
            showHeader={false}
          />
        }
        expandedFilters={filtersVisible}
        toggleFilters={toggleFilters}
      />

      {/* Error Alert */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -8, height: 0 }}
            animate={{ opacity: 1, y: 0, height: "auto" }}
            exit={{ opacity: 0, y: -8, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            <Alert
              severity="error"
              variant="standard"
              icon={<ErrorOutlineIcon />}
              action={
                <IconButton
                  aria-label="Fechar alerta de erro"
                  color="inherit"
                  size="small"
                  onClick={() => setError(null)}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
              sx={{
                mb: 3,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {error}
                </Typography>
                <Button
                  size="small"
                  color="error"
                  onClick={() => search()}
                  sx={{ ml: 1, textTransform: "none", fontWeight: 600 }}
                >
                  Tentar novamente
                </Button>
              </Box>
            </Alert>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Loading Skeleton */}
      {loading && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
        >
          <OrderListSkeleton />
        </motion.div>
      )}

      {/* Content */}
      {!loading && (
        <>
          {submitted ? (
            <motion.div variants={fadeIn} initial="hidden" animate="visible">
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.5, sm: 2.5 },
                  borderRadius: 3,
                  bgcolor: alpha(theme.palette.background.paper, 0.85),
                  backdropFilter: "blur(12px)",
                  border: `1px solid ${theme.palette.divider}`,
                  overflow: "hidden",
                }}
              >
                {/* Result count header */}
                {orders.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 2,
                      px: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontWeight: 500,
                      }}
                    >
                      {orders.length} pedido{orders.length !== 1 ? "s" : ""}{" "}
                      {showButtonMore ? "carregados" : "encontrados"}
                    </Typography>
                    {refreshing && (
                      <CircularProgress size={16} sx={{ opacity: 0.6 }} />
                    )}
                  </Box>
                )}

                {orders.length > 0 ? (
                  <>
                    <AnimatePresence mode="popLayout">
                      <motion.div
                        variants={staggerContainer}
                        initial="hidden"
                        animate="visible"
                      >
                        {orderRows}
                      </motion.div>
                    </AnimatePresence>

                    {/* Load More */}
                    <Button
                      title="Carregar mais"
                      size="large"
                      fullWidth
                      disabled={!showButtonMore || refreshing}
                      startIcon={
                        refreshing ? (
                          <CircularProgress size={18} />
                        ) : (
                          <ExpandCircleDownIcon />
                        )
                      }
                      onClick={() => loadOrdersHistory(false)}
                      sx={{
                        mt: 2,
                        py: 1.2,
                        borderRadius: 2,
                        color: theme.palette.text.secondary,
                        bgcolor: theme.palette.grey[50],
                        border: `1px solid ${theme.palette.grey[200]}`,
                        transition: "all 0.2s ease",
                        "&:hover": {
                          bgcolor: theme.palette.grey[100],
                          borderColor: theme.palette.grey[300],
                        },
                        "&.Mui-disabled": {
                          color: alpha(theme.palette.text.secondary, 0.4),
                          bgcolor: alpha(theme.palette.grey[100], 0.5),
                        },
                      }}
                    >
                      {refreshing
                        ? "Carregando..."
                        : showButtonMore
                        ? "Mostrar Mais Pedidos"
                        : "Todos os pedidos foram carregados"}
                    </Button>
                  </>
                ) : (
                  renderEmptyState()
                )}
              </Paper>
            </motion.div>
          ) : (
            renderEmptyState()
          )}

          {/* Report Modal */}
          <Dialog
            open={reportModalOpen}
            onClose={() => setReportModalOpen(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 3,
                maxHeight: "85vh",
              },
            }}
          >
            <DialogTitle
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                pb: 1,
              }}
            >
              <Typography variant="h6" fontWeight={600}>
                Relatório
              </Typography>
              <IconButton
                onClick={() => setReportModalOpen(false)}
                size="small"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
              <HistoryReportSummary
                filters={filters}
                reportData={reportData}
                reportLoading={reportLoading}
              />
            </DialogContent>
          </Dialog>
        </>
      )}
    </Box>
  );
}
